import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { Bell, X } from 'lucide-react';
import { isPushSubscribed, isPushSupported, registerPush, unregisterPush } from '../../services/push';
import BugReportButton from '../../components/BugReportButton';
import { DEFAULT_BRANDING, TABS, TIER_COLORS, TIER_EMOJI } from './constants';
import { BrandLogo } from './BrandLogo';
import { HomeTab } from './tabs/HomeTab';
import { PremiTab } from './tabs/PremiTab';
import { OrdiniTab } from './tabs/OrdiniTab';
import OrderPage from '../OrderPage';

/* ── MAIN DASHBOARD ────────────────────────────────────────── */
export function Dashboard({ data, setData, initialTab, token, pin }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'home');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushPromptDismissed, setPushPromptDismissed] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const brand = useMemo(() => ({ ...DEFAULT_BRANDING, ...(data.branding || {}) }), [data.branding]);
  const tc = brand.text_color || '#FFFFFF';
  const ts = brand.text_secondary_color || '#888888';
  const tierNames = data.tier_names || { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };
  const tierColorsCustom = data.tier_colors || {};
  const tierEmojis = data.tier_emojis || {};
  const tierColor = tierColorsCustom[data.tier] || TIER_COLORS[data.tier] || '#CD7F32';
  const tierEmoji = tierEmojis[data.tier] || TIER_EMOJI[data.tier] || '';
  const tierPct = data.next_tier_points
    ? Math.min(100, Math.round((data.lifetime_points / data.next_tier_points) * 100))
    : 100;

  // Check push subscription status on mount
  useEffect(() => {
    if (!isPushSupported()) return;
    isPushSubscribed().then(setPushEnabled);
    // Show prompt after 3s if not subscribed and not previously dismissed
    const dismissed = localStorage.getItem(`push_dismissed_${data.customer_id}`);
    if (!dismissed) {
      const timer = setTimeout(() => {
        isPushSubscribed().then(sub => { if (!sub) setShowPushPrompt(true); });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [data.customer_id]);

  const handlePushToggle = useCallback(async () => {
    if (pushEnabled) {
      await unregisterPush(data.customer_id);
      setPushEnabled(false);
      toast.success('Notifiche disattivate');
    } else {
      const result = await registerPush(data.customer_id);
      if (result) {
        setPushEnabled(true);
        setShowPushPrompt(false);
        toast.success('Notifiche attivate!');
      } else {
        toast.error('Impossibile attivare le notifiche. Controlla i permessi del browser.');
      }
    }
  }, [pushEnabled, data.customer_id]);

  const dismissPushPrompt = useCallback(() => {
    setShowPushPrompt(false);
    setPushPromptDismissed(true);
    localStorage.setItem(`push_dismissed_${data.customer_id}`, '1');
  }, [data.customer_id]);

  // Filter tabs:
  //  - hide "Sfide" if no challenges
  //  - hide tabs whose underlying module is globally disabled by superadmin
  //    (mapping module key → tab id).
  const visibleTabs = useMemo(() => {
    const moduleToTab = {
      shop: 'shop',                 // Merchandising
      games: 'giochi',
      coupons: 'coupon',
      menu: 'ordina',               // ordering / take-away
      'events-tickets': null,       // no dedicated tab, but kept for parity
    };
    const disabled = new Set(data.disabled_features || []);
    let tabs = TABS.filter(t => {
      // hide tab if any module mapped to it is disabled
      const matchingKeys = Object.entries(moduleToTab)
        .filter(([, tabId]) => tabId === t.id)
        .map(([k]) => k);
      return !matchingKeys.some(k => disabled.has(k));
    });
    return tabs;
  }, [data.disabled_features]);

  // If active tab is no longer visible (because a module was disabled), fall back to home.
  useEffect(() => {
    if (!visibleTabs.find(t => t.id === activeTab)) {
      setActiveTab('home');
    }
  }, [visibleTabs, activeTab]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: brand.background_color }} data-testid="customer-dashboard">
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: 'DM Sans, sans-serif' } }} />

      {/* Sticky Header - always visible */}
      <header className="px-4 pt-6 pb-4" style={{ background: `linear-gradient(to bottom, ${brand.background_color}, ${brand.background_color}dd)` }}>
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-2 mb-2">
            {brand.logo_url && <BrandLogo src={brand.logo_url} name={data.business_name} size={28} primaryColor={brand.primary_color} testid="header-brand-logo" />}
            <p className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: `${brand.primary_color}99` }}>{data.business_name}</p>
          </div>
          {/* Welcome message: ONLY rendered if the admin has configured
              one in /admin/branding → Bacheca tab. No legacy/default
              fallback — if it's empty, we just don't show anything. */}
          {data.dashboard_messages?.welcome?.title && (
            <h1 className="text-xl font-bold" style={{ color: tc }} data-testid="welcome-text">
              {data.dashboard_messages.welcome.title}
            </h1>
          )}
          {data.dashboard_messages?.welcome?.description && (
            <p className="text-sm mt-1" style={{ color: ts }} data-testid="welcome-description">
              {data.dashboard_messages.welcome.description}
            </p>
          )}
        </div>
      </header>

      {/* Scrollable content area — px-4 (16px lateral margins, native-app feel) */}
      <div className="flex-1 overflow-y-auto pb-28">
        <div className="max-w-lg mx-auto px-4">
          {/* Push notification prompt popup */}
          {showPushPrompt && isPushSupported() && (
            <div className="rounded-3xl p-5 border mb-5 animate-fade-in-up shadow-sm" style={{ backgroundColor: `${brand.primary_color}12`, borderColor: `${brand.primary_color}30` }} data-testid="push-prompt">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brand.primary_color}25` }}>
                  <Bell size={22} style={{ color: brand.primary_color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-bold" style={{ color: tc }}>Attiva le notifiche</p>
                  <p className="text-sm mt-1" style={{ color: ts }}>Ricevi aggiornamenti su premi, promozioni e novit&agrave; direttamente sul tuo dispositivo.</p>
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={handlePushToggle} data-testid="push-prompt-enable"
                      className="px-5 py-2.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-95"
                      style={{ backgroundColor: brand.primary_color }}>
                      Attiva
                    </button>
                    <button onClick={dismissPushPrompt} data-testid="push-prompt-dismiss"
                      className="px-4 py-2.5 rounded-2xl text-sm font-medium transition-all" style={{ color: ts }}>
                      Non ora
                    </button>
                  </div>
                </div>
                <button onClick={dismissPushPrompt} className="flex-shrink-0 mt-1" style={{ color: `${ts}66` }}>
                  <X size={18} />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'home' && <HomeTab data={data} brand={brand} tc={tc} ts={ts} tierColor={tierColor} tierEmoji={tierEmoji} tierNames={tierNames} tierColorsCustom={tierColorsCustom} tierPct={tierPct} pushEnabled={pushEnabled} onPushToggle={handlePushToggle} token={token} pin={pin} setData={setData} />}
          {activeTab === 'premi' && <PremiTab data={data} brand={brand} tc={tc} ts={ts} pushEnabled={pushEnabled} onPushToggle={handlePushToggle} token={token} pin={pin} />}
          {activeTab === 'ordini' && <OrdiniTab data={data} brand={brand} tc={tc} ts={ts} token={token} pin={pin} />}
          {activeTab === 'ordina' && <OrderPage embedded presetToken={token} presetPin={pin} presetCustomerId={data.customer_id} presetName={data.customer?.name || data.name || ''} presetPoints={data.customer?.points ?? data.points ?? 0} />}
        </div>
      </div>

      {/* Bottom Tab Bar — mobile-first, generous touch targets (h-20),
          thumb-zone optimized, frosted-glass backdrop */}
      <nav className="fixed bottom-0 left-0 right-0 border-t backdrop-blur-xl z-50" style={{ backgroundColor: `${brand.background_color}ee`, borderColor: `${tc}0a` }} data-testid="tab-bar">
        <div className="max-w-lg mx-auto flex items-stretch justify-around h-20 px-2">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
                className="flex-1 flex flex-col items-center justify-center gap-1 px-2 active:scale-95 transition-transform"
              >
                <Icon size={26} style={{ color: active ? brand.primary_color : `${ts}aa` }} strokeWidth={active ? 2.5 : 1.75} />
                <span className="text-[12px] font-semibold tracking-tight" style={{ color: active ? brand.primary_color : `${ts}aa` }}>{tab.label}</span>
              </button>
            );
          })}
        </div>
        {/* Safe area padding for iPhones */}
        <div className="h-[env(safe-area-inset-bottom)]" style={{ backgroundColor: `${brand.background_color}ee` }} />
      </nav>
      <BugReportButton userRole="cliente" userName={data?.name || ''} brandName={data?.business_name || ''} />
    </div>
  );
}
